import { useState } from "react";
import { Bot, AlertTriangle, CheckCircle, XCircle, Loader2, Code } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

interface RobotsDirective {
  directive: string;
  value: string;
  impact: "positive" | "negative" | "neutral";
  description: string;
}

interface RobotsResult {
  isValid: boolean;
  directives: RobotsDirective[];
  issues: string[];
  recommendations: string[];
  parsedMeta: {
    index: boolean;
    follow: boolean;
    archive: boolean;
    snippet: boolean;
    imageIndex: boolean;
  };
}

export const RobotsMetaChecker = () => {
  const [metaTag, setMetaTag] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<RobotsResult | null>(null);

  const sampleTags = [
    '<meta name="robots" content="index, follow">',
    '<meta name="robots" content="noindex, nofollow">',
    '<meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large">',
    '<meta name="googlebot" content="noarchive, notranslate">',
    '<meta name="robots" content="noindex, follow, noarchive">'
  ];

  const loadSample = () => {
    const sample = sampleTags[Math.floor(Math.random() * sampleTags.length)];
    setMetaTag(sample);
    setResult(null);
  };

  const analyzeRobots = () => {
    if (!metaTag.trim()) {
      toast.error("Please enter a robots meta tag to analyze");
      return;
    }

    setIsAnalyzing(true);

    setTimeout(() => {
      const directives: RobotsDirective[] = [];
      const issues: string[] = [];
      const recommendations: string[] = [];

      // Extract content from meta tag
      const contentMatch = metaTag.match(/content\s*=\s*["']([^"']+)["']/i);
      const nameMatch = metaTag.match(/name\s*=\s*["']([^"']+)["']/i);
      
      if (!contentMatch) {
        setResult({
          isValid: false,
          directives: [],
          issues: ["Could not parse meta tag. Ensure it follows the format: <meta name=\"robots\" content=\"...\">"],
          recommendations: ["Use the correct meta tag format with name and content attributes"],
          parsedMeta: { index: true, follow: true, archive: true, snippet: true, imageIndex: true }
        });
        setIsAnalyzing(false);
        return;
      }

      const content = contentMatch[1].toLowerCase();
      const tagName = nameMatch ? nameMatch[1].toLowerCase() : "robots";
      const directiveList = content.split(",").map(d => d.trim());

      // Default behaviors
      const parsed = {
        index: true,
        follow: true,
        archive: true,
        snippet: true,
        imageIndex: true
      };

      // Analyze each directive
      directiveList.forEach(directive => {
        const [key, value] = directive.split(":").map(s => s.trim());

        switch (key) {
          case "index":
            parsed.index = true;
            directives.push({
              directive: "index",
              value: "",
              impact: "positive",
              description: "Page will be indexed by search engines"
            });
            break;

          case "noindex":
            parsed.index = false;
            directives.push({
              directive: "noindex",
              value: "",
              impact: "negative",
              description: "Page will NOT be indexed - won't appear in search results"
            });
            break;

          case "follow":
            parsed.follow = true;
            directives.push({
              directive: "follow",
              value: "",
              impact: "positive",
              description: "Search engines will follow links on this page"
            });
            break;

          case "nofollow":
            parsed.follow = false;
            directives.push({
              directive: "nofollow",
              value: "",
              impact: "negative",
              description: "Search engines will NOT follow links - no link equity passed"
            });
            break;

          case "noarchive":
            parsed.archive = false;
            directives.push({
              directive: "noarchive",
              value: "",
              impact: "neutral",
              description: "Search engines won't show cached version of this page"
            });
            break;

          case "nosnippet":
            parsed.snippet = false;
            directives.push({
              directive: "nosnippet",
              value: "",
              impact: "negative",
              description: "No text snippet or video preview shown in search results"
            });
            break;

          case "max-snippet":
            directives.push({
              directive: "max-snippet",
              value: value || "-1",
              impact: value === "-1" ? "positive" : "neutral",
              description: value === "-1" 
                ? "No limit on snippet length - Google can show full description"
                : `Snippet limited to ${value} characters`
            });
            break;

          case "max-image-preview":
            directives.push({
              directive: "max-image-preview",
              value: value || "standard",
              impact: value === "large" ? "positive" : "neutral",
              description: `Image previews set to ${value || "standard"} size in search results`
            });
            break;

          case "max-video-preview":
            directives.push({
              directive: "max-video-preview",
              value: value || "-1",
              impact: "neutral",
              description: value === "-1" 
                ? "No limit on video preview length"
                : `Video preview limited to ${value} seconds`
            });
            break;

          case "noimageindex":
            parsed.imageIndex = false;
            directives.push({
              directive: "noimageindex",
              value: "",
              impact: "negative",
              description: "Images on this page won't appear in Google Images"
            });
            break;

          case "notranslate":
            directives.push({
              directive: "notranslate",
              value: "",
              impact: "neutral",
              description: "Google won't offer translation for this page"
            });
            break;

          case "none":
            parsed.index = false;
            parsed.follow = false;
            directives.push({
              directive: "none",
              value: "",
              impact: "negative",
              description: "Equivalent to noindex, nofollow - page completely hidden from search"
            });
            break;

          case "all":
            directives.push({
              directive: "all",
              value: "",
              impact: "positive",
              description: "Default behavior - index page and follow links"
            });
            break;
        }
      });

      // Generate issues and recommendations
      if (!parsed.index) {
        issues.push("This page will NOT appear in search results due to noindex directive");
        recommendations.push("Remove noindex if you want this page to rank in search engines");
      }

      if (!parsed.follow) {
        issues.push("Links on this page won't pass SEO value (link equity)");
        recommendations.push("Consider using follow to help distribute page authority through internal links");
      }

      if (!parsed.snippet) {
        issues.push("No description snippet will show in search results, potentially reducing click-through rate");
      }

      if (tagName === "googlebot" && !metaTag.includes('name="robots"')) {
        recommendations.push("Consider adding a general robots meta tag in addition to googlebot-specific directives");
      }

      if (parsed.index && parsed.follow) {
        recommendations.push("Your page is set for optimal search engine visibility");
      }

      setResult({
        isValid: true,
        directives,
        issues,
        recommendations,
        parsedMeta: parsed
      });
      
      setIsAnalyzing(false);
    }, 600);
  };

  return (
    <div className="section-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Bot className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Robots Meta Tag Checker
          </h3>
          <p className="text-sm text-muted-foreground">
            Analyze robots directives and their SEO impact
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-foreground">
              Robots Meta Tag
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadSample}
              className="text-xs text-primary hover:text-primary/80"
            >
              Load Sample
            </Button>
          </div>
          <div className="relative">
            <Code className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={metaTag}
              onChange={(e) => {
                setMetaTag(e.target.value);
                setResult(null);
              }}
              placeholder='<meta name="robots" content="index, follow">'
              className="pl-10 font-mono text-sm"
            />
          </div>
        </div>

        <Button
          onClick={analyzeRobots}
          disabled={isAnalyzing || !metaTag.trim()}
          className="w-full"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Bot className="w-4 h-4 mr-2" />
              Analyze Directives
            </>
          )}
        </Button>

        {result && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className={`p-3 rounded-xl border text-center ${result.parsedMeta.index ? "bg-success/10 border-success/20" : "bg-destructive/10 border-destructive/20"}`}>
                {result.parsedMeta.index ? <CheckCircle className="w-5 h-5 text-success mx-auto mb-1" /> : <XCircle className="w-5 h-5 text-destructive mx-auto mb-1" />}
                <p className="text-xs font-medium text-foreground">Indexable</p>
              </div>
              <div className={`p-3 rounded-xl border text-center ${result.parsedMeta.follow ? "bg-success/10 border-success/20" : "bg-destructive/10 border-destructive/20"}`}>
                {result.parsedMeta.follow ? <CheckCircle className="w-5 h-5 text-success mx-auto mb-1" /> : <XCircle className="w-5 h-5 text-destructive mx-auto mb-1" />}
                <p className="text-xs font-medium text-foreground">Follow Links</p>
              </div>
              <div className={`p-3 rounded-xl border text-center ${result.parsedMeta.snippet ? "bg-success/10 border-success/20" : "bg-warning/10 border-warning/20"}`}>
                {result.parsedMeta.snippet ? <CheckCircle className="w-5 h-5 text-success mx-auto mb-1" /> : <AlertTriangle className="w-5 h-5 text-warning mx-auto mb-1" />}
                <p className="text-xs font-medium text-foreground">Snippets</p>
              </div>
              <div className={`p-3 rounded-xl border text-center ${result.parsedMeta.imageIndex ? "bg-success/10 border-success/20" : "bg-warning/10 border-warning/20"}`}>
                {result.parsedMeta.imageIndex ? <CheckCircle className="w-5 h-5 text-success mx-auto mb-1" /> : <AlertTriangle className="w-5 h-5 text-warning mx-auto mb-1" />}
                <p className="text-xs font-medium text-foreground">Images</p>
              </div>
            </div>

            {/* Directives */}
            {result.directives.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Parsed Directives</h4>
                <div className="space-y-2">
                  {result.directives.map((directive, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border ${
                        directive.impact === "positive" 
                          ? "bg-success/5 border-success/20" 
                          : directive.impact === "negative"
                          ? "bg-destructive/5 border-destructive/20"
                          : "bg-muted/50 border-border"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm font-bold text-foreground">
                          {directive.directive}{directive.value ? `:${directive.value}` : ""}
                        </span>
                        {directive.impact === "positive" && <CheckCircle className="w-4 h-4 text-success" />}
                        {directive.impact === "negative" && <XCircle className="w-4 h-4 text-destructive" />}
                      </div>
                      <p className="text-xs text-muted-foreground">{directive.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Issues */}
            {result.issues.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-warning" />
                  Potential Issues
                </h4>
                <ul className="space-y-1">
                  {result.issues.map((issue, index) => (
                    <li key={index} className="text-sm text-warning flex items-start gap-2">
                      <span>•</span>
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommendations */}
            {result.recommendations.length > 0 && (
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                <h4 className="text-sm font-medium text-foreground mb-2">Recommendations</h4>
                <ul className="space-y-1">
                  {result.recommendations.map((rec, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-primary">•</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
